import { style } from "typestyle";
import { getContentUrl } from "../../StaticAssets";
import { TableauLocalizedStrings } from "../../Strings";
import { typeStyleTargetId } from "../TypeStyleSetup";

export enum InstantAnalyticsException {
  GENERIC,
  FORBIDDEN,
}

const dataProviderLookup: Record<string, string> = {
  cdp: "Salesforce Data Cloud",
  crma: "Salesforce CRMA",
  dbx: "Databricks",
};

export const exceptionFrom = (
  status: number
): InstantAnalyticsException | null => {
  switch (status) {
    case 403:
      return InstantAnalyticsException.FORBIDDEN;
    case 404:
      return InstantAnalyticsException.GENERIC;
    default:
      return null;
  }
};

function updatePageStyling(doc: Document) {
  Array.from(doc.querySelectorAll('style,link[rel="stylesheet"]'))
    .filter((element) => element.id !== typeStyleTargetId)
    .forEach((element) => element.remove());
}

function extractDataProvider(pathname: string): string {
  const pathSegments = pathname.split("/");

  // Check if path has enough segments
  if (pathSegments.length < 4) {
    throw new Error(
      `Invalid path format: ${pathname}. Expected format: /vizql/showInstantAnalytics/{provider}/key/check`
    );
  }

  const rawDataProvider = pathSegments[3];

  // Check if data provider segment is empty
  if (!rawDataProvider) {
    throw new Error("Data provider segment is empty in the path");
  }

  // Look up the display name, throw error if not found
  const displayName = dataProviderLookup[rawDataProvider];
  if (!displayName) {
    throw new Error(
      `Unknown data provider: ${rawDataProvider}. Supported providers are: ${Object.keys(
        dataProviderLookup
      ).join(", ")}`
    );
  }

  return displayName;
}

export function showInstantAnalyticsErrorPageWithException(
  doc: Document,
  strings: TableauLocalizedStrings,
  exception: InstantAnalyticsException,
  pathname: string
) {
  const renderer = new InstantAnalyticsErrorPageRenderer(doc, strings);

  let dataProvider: string;
  try {
    dataProvider = extractDataProvider(pathname);
  } catch (error) {
    console.error("Error processing data provider:", error);
    dataProvider = "";
  }

  const locString = stringIDFrom(exception, strings, dataProvider);
  updatePageStyling(doc);

  doc.body.replaceChildren(renderer.createContentContainer(locString));
}

export function showInstantAnalyticsErrorPageWithLocalizedErrorMessage(
  doc: Document,
  strings: TableauLocalizedStrings,
  localizedExceptionMessage: string,
  newPageTitle?: string
) {
  const renderer = new InstantAnalyticsErrorPageRenderer(doc, strings);

  if (newPageTitle) {
    doc.title = newPageTitle;
  }

  updatePageStyling(doc);

  doc.body.replaceChildren(
    renderer.createContentContainer(localizedExceptionMessage)
  );
}

const stringIDFrom = (
  exception: InstantAnalyticsException,
  strings: TableauLocalizedStrings,
  dataProvider: string
): string => {
  switch (exception) {
    case InstantAnalyticsException.FORBIDDEN:
      return strings.getString("InstantAnalyticsPermissionsExceptionMessage");
    default:
      return strings.InstantAnalyticsGenericExceptionMessage(dataProvider);
  }
};

const IconStyles = (svgUrl: string): string =>
  style({
    backgroundImage: `url(${getContentUrl(svgUrl)})`,
    height: "60px",
    width: "60px",
    marginBottom: "16px",
  });

class InstantAnalyticsErrorPageRenderer {
  doc: Document;
  strings: TableauLocalizedStrings;

  constructor(doc: Document, strings: TableauLocalizedStrings) {
    this.doc = doc;
    this.strings = strings;
  }

  public createContentContainer(subheadingText: string): HTMLDivElement {
    const container = this.doc.createElement("div");
    container.className = style({
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      textAlign: "center",
      position: "fixed",
      height: "100%",
      width: "100%",
    });
    container.appendChild(this.workbookIcon());
    container.appendChild(this.heading());
    container.appendChild(this.subheading(subheadingText));
    return container;
  }

  private heading() {
    const p = this.doc.createElement("p");
    p.className = style({
      color: "#333333",
      fontSize: "18px",
      fontFamily: "Benton Sans, sans-serif",
      marginBottom: "14px",
    });
    p.textContent = this.strings.getString("InstantAnalyticsErrorPageHeading");
    return p;
  }

  private subheading(text: string) {
    const p = this.doc.createElement("p");
    p.className = style({
      fontSize: "14px",
      fontFamily: "Benton Sans, sans-serif",
    });
    p.textContent = text;
    return p;
  }

  private workbookIcon() {
    const svgUrl = "images/workbook-spot-icon.svg";
    const div = this.doc.createElement("div");
    div.className = IconStyles(svgUrl);
    return div;
  }
}
